import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Check } from 'lucide-react';

interface WelcomeTourProps {
    run: boolean;
    onClose: () => void;
}

const TOUR_STEPS = [
    {
        target: 'tour-health-story',
        title: "Start Your Health Story",
        content: "NatureNani works best when she knows you. Start by sharing your 'Health Story'—this helps us bridge ancient wisdom to your unique body, ensuring every suggestion is safe, verified, and personalized to your body's nature.",
        position: 'bottom' // Tooltip position relative to target
    },
    {
        target: 'tour-consultation-hub',
        title: "Your Consultation Hub",
        content: "What is your body telling today? Type in any symptom, question, or curiosity. I’ll search through 37,000+ verified knowledge points to identify root causes.",
        position: 'top'
    },
    {
        target: 'tour-healing-rituals',
        title: "Daily Healing Rituals",
        content: "Not sure where to begin? Simply ask NatureNani for personalized seasonal recipes, yoga routines, or herbal wisdom to unlock rituals tailored to your nature.",
        position: 'top'
    }
];

const WelcomeTour: React.FC<WelcomeTourProps> = ({ run, onClose }) => {
    const [stepIndex, setStepIndex] = useState(0);
    const [style, setStyle] = useState<{ top?: number; left?: number; transform?: string }>({});
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (!run) return;

        // Check if already seen
        const seen = localStorage.getItem('nani_welcome_tour_seen');
        if (seen) {
            onClose();
            return;
        }

        setIsVisible(true);
    }, [run, onClose]);

    useEffect(() => {
        if (!isVisible) return;

        const step = TOUR_STEPS[stepIndex];
        const element = document.getElementById(step.target);

        if (element) {
            // Scroll into view if needed
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [stepIndex, isVisible]);

    const handleNext = () => {
        if (stepIndex < TOUR_STEPS.length - 1) {
            setStepIndex(prev => prev + 1);
        } else {
            handleComplete();
        }
    };

    const handleComplete = () => {
        localStorage.setItem('nani_welcome_tour_seen', 'true');
        setIsVisible(false);
        setTimeout(onClose, 300);
    };

    if (!isVisible) return null;

    const currentStep = TOUR_STEPS[stepIndex];

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 pointer-events-none" // Overlay wrapper
            >
                {/* Visual Backdrop - Click to dismiss */}
                <div className="absolute inset-0 bg-transparent pointer-events-auto" onClick={handleComplete}></div>

                {/* Tooltip Card - Static Top Right Position */}
                <motion.div
                    layout
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="fixed top-24 right-4 md:right-8 w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 pointer-events-auto border border-sage-100 z-[60]"
                >
                    <div className="relative z-10">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-[10px] font-bold text-sage-500 uppercase tracking-widest">
                                Tip {stepIndex + 1} of {TOUR_STEPS.length}
                            </span>
                            <button onClick={handleComplete} className="text-gray-400 hover:text-gray-600">
                                <X size={16} />
                            </button>
                        </div>

                        <h4 className="font-serif font-bold text-lg text-sage-900 mb-2">{currentStep.title}</h4>
                        <p className="text-sm text-gray-600 leading-relaxed mb-6">{currentStep.content}</p>

                        <div className="flex items-center justify-between">
                            <div className="flex gap-1.5">
                                {TOUR_STEPS.map((_, i) => (
                                    <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === stepIndex ? 'bg-sage-600' : 'bg-gray-200'}`}></div>
                                ))}
                            </div>
                            <button
                                onClick={handleNext}
                                className="flex items-center gap-2 px-4 py-2 bg-sage-600 text-white rounded-lg text-xs font-bold hover:bg-sage-700 transition-colors shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
                            >
                                {stepIndex === TOUR_STEPS.length - 1 ? 'Launch App' : 'Next'}
                                {stepIndex === TOUR_STEPS.length - 1 ? <Check size={14} /> : <ArrowRight size={14} />}
                            </button>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default WelcomeTour;
